import { useEffect, useState } from 'react';
import { Link } from '../app/router';
import { usePrefs } from '../app/prefs';
import { useLaunchInfo } from '../data/launch';
import { racePresentation } from '../data/racePresentation';
import { countdownText, DEFAULT_RACE_MINUTES, halloweenEvent, runDayPhase, runDayStarts, usdFromUnits, type RunDayEvent } from '../data/runDay';
import type { WorldView } from '../data/types';
import '../styles/runday.css';

/** RUN DAY event from /launch.json, or null when the flag is off. */
export function useRunDay(): RunDayEvent | null {
  return halloweenEvent(useLaunchInfo());
}

/** Marks <html data-event="halloween"> while the flag is on, so the theme accents apply site-wide. */
export function useEventTheme() {
  const on = !!useRunDay();
  useEffect(() => {
    if (!on) return;
    document.documentElement.dataset.event = 'halloween';
    return () => { delete document.documentElement.dataset.event; };
  }, [on]);
}

function useNow(ms = 1000) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), ms);
    return () => clearInterval(t);
  }, [ms]);
  return now;
}

const usd = (n: number | null) => (n === null ? '—' : `$${n.toLocaleString(undefined, { maximumFractionDigits: n >= 100 ? 0 : 2, minimumFractionDigits: 0 })}`);
const localTime = new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit' });
const utcTime = new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' });
const localDay = new Intl.DateTimeFormat(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
const zoneName = () => new Intl.DateTimeFormat(undefined, { timeZoneName: 'short' }).formatToParts(new Date()).find((p) => p.type === 'timeZoneName')?.value ?? 'local';

/** Burn pot (Forge reserve) and Golden Shoe jackpot (prize reserve) in USD, from the indexed treasury. Null = unknown. */
export function runDayPots(view: WorldView) {
  const b = view.treasury?.buckets;
  const dec = view.quote.decimals;
  const shoe = view.run?.shoe;
  const live = ['live', 'resolving', 'starting'].includes(racePresentation(view).kind);
  // While a race's shoe is open the jackpot sits in that race's escrow, so show the onchain shoe instead of the reserve.
  const inRace = live && shoe && shoe.status !== 'None' ? usdFromUnits(shoe.amount, dec) : null;
  return { burn: usdFromUnits(b?.forgeReserve, dec), jackpot: inRace ?? usdFromUnits(b?.prizeReserve, dec) };
}

const Bat = ({ className }: { className: string }) => (
  <svg className={className} viewBox="0 0 64 28" aria-hidden="true" focusable="false">
    <path d="M32 9c2-4 3-6 3-8 1 2 1 4 0 6 4-2 9-3 14-1 5 2 9 6 14 6-4 2-7 5-8 10-2-3-6-4-9-3 0-3-3-5-6-5-3 0-5 3-6 7l-2-5-2 5c-1-4-3-7-6-7s-6 2-6 5c-3-1-7 0-9 3-1-5-4-8-8-10 5 0 9-4 14-6 5-2 10-1 14 1-1-2-1-4 0-6 0 2 1 4 3 8z" />
  </svg>
);

const Pumpkin = ({ size = 28 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 32 32" aria-hidden="true" focusable="false" className="runday-pumpkin">
    <path d="M16 7c1-3 3-4 5-4-2 1-3 3-3 5z" fill="#6b7a3a" />
    <ellipse cx="10.5" cy="18" rx="7" ry="10" fill="#C95424" />
    <ellipse cx="21.5" cy="18" rx="7" ry="10" fill="#C95424" />
    <ellipse cx="16" cy="18" rx="7.5" ry="11" fill="#EC673A" />
    <path d="M10 15l3 2-3 1zM22 15l-3 2 3 1zM10 22c2 2 4 1 6 3 2-2 4-1 6-3-2 4-10 4-12 0z" fill="#EAAF43" />
  </svg>
);

/**
 * RUN DAY: countdown, schedule, the two pots and how to play, in the runner's own voice. Spooky but on brand: ink card,
 * cream type, orange and gold. Decorations are CSS-only and stop under reduced motion (system or the app setting).
 */
export function RunDayPanel({ view, event }: { view: WorldView; event: RunDayEvent }) {
  const now = useNow();
  const prefs = usePrefs();
  const starts = runDayStarts(event);
  const raceMinutes = event.raceMinutes ?? DEFAULT_RACE_MINUTES;
  const phase = runDayPhase(starts, now, raceMinutes);
  const total = starts.length;
  const forks = event.forks ?? 3;
  const cap = event.forkBurnCap ?? 50;
  const name = event.name ?? 'RUN DAY';
  const chainLive = ['live', 'resolving', 'starting'].includes(racePresentation(view).kind);
  const racing = phase.kind === 'live' || chainLive;
  // Collapsed to a strip while a race runs so the course stays above the fold; the viewer can still open it.
  const [open, setOpen] = useState<boolean | null>(null);
  const expanded = open ?? !racing;
  const { burn, jackpot } = runDayPots(view);
  const lastStart = starts[total - 1];
  // A fuse from the first race to midnight UTC after the Witching Hour.
  const midnight = Date.UTC(new Date(lastStart).getUTCFullYear(), new Date(lastStart).getUTCMonth(), new Date(lastStart).getUTCDate() + 1);
  const fuse = Math.max(0, Math.min(1, (midnight - now) / (midnight - starts[0])));

  const raceLabel = (i: number) => (i === total - 1 ? 'the Witching Hour' : `race ${i + 1} of ${total}`);
  let kicker: string, clock: string | null, sub: string;
  switch (phase.kind) {
    case 'before':
      kicker = 'First race in';
      clock = countdownText(phase.next - now);
      sub = `${localDay.format(phase.next)} · ${localTime.format(phase.next)} your time · ${utcTime.format(phase.next)} UTC`;
      break;
    case 'live':
      kicker = phase.index === total - 1 ? `Race ${total} of ${total} · the Witching Hour` : `Race ${phase.index + 1} of ${total} · on the course now`;
      clock = phase.next ? countdownText(phase.next - now) : null;
      sub = phase.next ? `until ${raceLabel(phase.index + 1)} · ${localTime.format(phase.next)} your time` : 'Last race. Everything left in the burn pot burns.';
      break;
    case 'between':
      kicker = `Next: ${phase.index === total - 1 ? `the Witching Hour (race ${total} of ${total})` : `race ${phase.index + 1} of ${total}`}`;
      clock = countdownText(phase.next - now);
      sub = `${localTime.format(phase.next)} your time · ${utcTime.format(phase.next)} UTC`;
      break;
    default:
      kicker = `${name} is over`;
      clock = null;
      sub = 'Six races run. Thanks for running with me. The jackpot rolls over to the next race.';
  }

  return (
    <section className="runday" aria-labelledby="runday-h" data-testid="runday" data-phase={phase.kind} data-motion={prefs.reducedMotion ? 'reduced' : 'full'}>
      <div className="runday-sky" aria-hidden="true">
        <span className="runday-moon" />
        <Bat className="runday-bat b1" />
        <Bat className="runday-bat b2" />
        <Bat className="runday-bat b3" />
        <span className="runday-fog f1" />
        <span className="runday-fog f2" />
      </div>
      <div className="runday-top">
        <div className="runday-title">
          <span className="runday-date"><Pumpkin size={18} /> {localDay.format(starts[0])} · Robinhood Chain</span>
          <h2 id="runday-h">{name}</h2>
          <p className="runday-tag">{total} races. {forks * total} haunted forks. My whole burn pot rides on it.</p>
        </div>
        <div className="runday-clock" data-testid="runday-clock">
          <span className="runday-kicker">{kicker}</span>
          {clock && <strong className="runday-count" role="timer" aria-live="off">{clock}</strong>}
          <span className="runday-sub">{sub}</span>
        </div>
      </div>

      <div className="runday-meters">
        <div className="runday-meter burn" data-testid="runday-burn">
          <span className="runday-meter-label">Burn pot</span>
          <strong>{usd(burn)}</strong>
          <div className="runday-fuse" role="meter" aria-label="Time left before the pot burns" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(fuse * 100)} aria-valuetext={phase.kind === 'done' ? 'Burned' : `${countdownText(Math.max(0, midnight - now))} until midnight UTC`}>
            <span style={{ width: `${fuse * 100}%` }} />
          </div>
          <small>Forge gates burn up to ${cap} each; up to $200 in the Witching Hour.</small>
        </div>
        <div className="runday-meter jackpot" data-testid="runday-jackpot">
          <span className="runday-meter-label">Golden Shoe jackpot</span>
          <strong>{usd(jackpot)}</strong>
          <div className="runday-glow" aria-hidden="true"><span /></div>
          <small>Rolls over until someone catches it.</small>
        </div>
      </div>
      {(burn === null || jackpot === null) && <p className="runday-note">Reading the pots from the chain… amounts appear when the treasury feed is live.</p>}

      <button type="button" className="runday-toggle" aria-expanded={expanded} aria-controls="runday-more" onClick={() => setOpen(!expanded)}>
        {expanded ? 'Hide schedule and how to play' : 'Schedule and how to play'}
      </button>

      <div id="runday-more" className="runday-more" hidden={!expanded}>
        <div className="runday-schedule">
          <h3>Schedule <span>{zoneName()} · UTC</span></h3>
          <ol>
            {starts.map((t, i) => {
              const state = now >= t + raceMinutes * 60_000 ? 'done' : now >= t ? 'live' : (phase.kind === 'before' || phase.kind === 'between') && phase.index === i ? 'next' : 'later';
              return (
                <li key={t} data-state={state} className={i === total - 1 ? 'witching' : undefined}>
                  <span className="runday-n">{i + 1}</span>
                  <span className="runday-when"><strong>{localTime.format(t)}</strong><small>{utcTime.format(t)} UTC</small></span>
                  <span className="runday-what">{i === total - 1 ? <><strong>The Witching Hour</strong><small>Forge gates burn up to $200</small></> : <small>{forks} haunted forks · {raceMinutes} min</small>}</span>
                  <span className="runday-state">{state === 'done' ? 'Run' : state === 'live' ? 'Live' : state === 'next' ? 'Next' : ''}</span>
                </li>
              );
            })}
          </ol>
        </div>

        <div className="runday-how">
          <h3>How to haunt me</h3>
          <ul>
            <li className="buy">
              <strong>Buy for a jackpot ticket.</strong>
              <span>Buy RUNNER on runner.party while my ticket window is open (it opens about 4 minutes before I start and closes before I can reach the shoe). Bigger buy, more weight. Catch the Golden Shoe and someone takes the jackpot.</span>
            </li>
            <li className="sell">
              <strong>Sell to burn.</strong>
              <span>Every race has {forks} haunted forks. Sells steer me left, into the Forge gate: it buys back and burns up to ${cap} of RUNNER.</span>
            </li>
            <li className="stake">
              <strong>Stake to drink at the water.</strong>
              <span>Buys steer me right, to the water station, and it pays stakers. <Link to="/rewards">Stake RUNNER</Link> and you earn every time I stop for a drink.</span>
            </li>
          </ul>
        </div>
      </div>
    </section>
  );
}
